import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { getDisplayOptionsFromRedux } from 'AppSrc/core/components/forms/form-base-components/redux/display-options/get-display-options-from-redux';

import PropTypes from 'prop-types';
import { withClickOutside } from 'AppSrc/core/components/utility/with-click-outside';
import {
  STANDARD_DROPDOWN,
  getSelectedOptionForDropdown,
  isDropDownATypeahead,
} from 'AppSrc/core/components/forms/form-base-components/sub-components/drop-down-components/templates/dropdown-templates';
import { clearTypeaheadListForFieldId } from 'AppSrc/core/actions/actions-typeahead-lists';
import { InlineErrors } from './sub-components/inline-errors';
import { FieldTitle } from './sub-components/field-title';
import {
  vFButtonSelectorOptionShape,
} from './data-structure/v-f-proptypes';
import { TemplatedOptionButtonHead } from './sub-components/drop-down-components/templated-option-button-head';
import { DropDownOptions } from './sub-components/drop-down-components/drop-down-options';
import 'AppSrc/core/style/components/form/form-base-components/v-f-dropdown.css';




/*
  Base component for VFDropdowns that is not tightly coupled to formik
  This can be used outside of a formik context in the system
*/
class VFDropdownBaseComponent extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      dropdownOpen: false,
    };
    this.toggleOpenDropdown = this.toggleOpenDropdown.bind(this);
    this.openDropdown = this.openDropdown.bind(this);
    this.updateValue = this.updateValue.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);
    this.clearValue = this.clearValue.bind(this);
    this.focusAfter = this.focusAfter.bind(this);
    this.afterComponentRef = React.createRef();
  }


  componentDidMount() {
    const getDisplayOptionsOnMount = this.props.getDisplayOptionsOnMount;
    if (getDisplayOptionsOnMount) {
      this.props.dispatchGetDisplayOptionsOnMount();
    }
  }

  // Close the selector when clicking outside
  // called by the withClickOutside HOC
  onClickOutside() {
    // if the component isn't in open mode, do nothing when clicking about the place
    if (this.state.dropdownOpen !== false) {
      this.setState({ dropdownOpen: false });
    }
  }

  focusAfter() {
    this.afterComponentRef.current.focus();
  }

  updateValue(value) {
    const {
      optionTemplate,
      name,
      getDisplayOptionsOnUpdate,
    } = this.props;
    this.props.updateValue(value);
    this.setState({
      dropdownOpen: false,
    });

    if (getDisplayOptionsOnUpdate) {
      this.props.dispatchGetDisplayOptionsOnUpdate(value);
    }

    if (isDropDownATypeahead(optionTemplate)) {
      this.props.clearTypeaheadListForFieldId(name);
    }
    // move focus to after the component
    this.focusAfter();
    return null;
  }

  clearValue(e) {

    e.preventDefault();
    const {
      optionTemplate,
      name,
      getDisplayOptionsOnUpdate,
    } = this.props;
    this.props.updateValue(null);
    this.setState({
      dropdownOpen: false,
    });

    if (getDisplayOptionsOnUpdate) {
      this.props.dispatchGetDisplayOptionsOnUpdate(null);
    }

    if (isDropDownATypeahead(optionTemplate)) {
      this.props.clearTypeaheadListForFieldId(name);
    }
    this.focusAfter();
    return null;
  }

  toggleOpenDropdown() {
    this.setState((prevState) => ({
      dropdownOpen: !prevState.dropdownOpen,
    }));
  }

  openDropdown() {
    this.setState({
      dropdownOpen: true,
    });
  }

  render() {

    const {
      displayOptions,
      name,
      value,
      optional,
      disabled,
      readOnly,
      fieldTitle,
      placeholder,
      optionTemplate,
      inlineErrors,
      touched,
      noClear,
    } = this.props;

    const {
      dropdownOpen,
    } = this.state;

    const fieldDisabled = disabled || displayOptions.length === 0;

    const selectedValue = value ? value : null;

    const dropdownTemplate = optionTemplate ? optionTemplate : STANDARD_DROPDOWN;
    const selectedOption = getSelectedOptionForDropdown({
      optionTemplate: dropdownTemplate,
      displayOptions,
      value: selectedValue,
    });
    const selectedDisplayValue = selectedOption ? selectedOption.text : '';





    const classNameBase = 'v-f-dropdown';

    const classNameList = [
      'v-f-field-type',
      classNameBase,
      `${classNameBase}-${name}`,
    ];

    if (fieldDisabled) {
      classNameList.push(`${classNameBase}-disabled`);
    }
    if (readOnly) {
      classNameList.push(`${classNameBase}-read-only`);
    }

    const className = classNameList.join(' ');

    const clearValue = noClear ? null : this.clearValue;

    return (

      <div
        className={className}
        name={name}
      >
        {
          fieldTitle && (
            <FieldTitle
              fieldTitle={fieldTitle}
              optional={optional}
            />
          )
        }
        <div className={`v-f-dropdown-menu v-f-dropdown-menu-${dropdownTemplate}`}>
          <TemplatedOptionButtonHead
            toggleOpenDropdown={this.toggleOpenDropdown}
            openDropdown={this.openDropdown}
            selectedDisplayValue={selectedDisplayValue}
            placeholder={placeholder}
            disabled={fieldDisabled || readOnly}
            optionTemplate={dropdownTemplate}
            dropdownOpen={dropdownOpen}
            fieldId={name}
            clearValue={clearValue}
          />
          {
            dropdownOpen && !fieldDisabled && (
              <DropDownOptions
                updateValue={this.updateValue}
                optionTemplate={dropdownTemplate}
                displayOptions={displayOptions}
              />
            )
          }
          {
            inlineErrors && touched && (
              <InlineErrors
                inlineErrors={inlineErrors}
              />
            )
          }
        </div>
        <div
          className="hidden-but-can-focus"
          // this div is here purely to allow us to capture the focus once a value has been selected
          // this allows a tab press to pass to the next form element.
          // NB in chrome, although the focus passes to the body after selecting and option,
          // the browser remembers the previous tabbed item. Unfortunately this doesn't work in IE11
          tabIndex="-1"
          ref={this.afterComponentRef}
        />
      </div>
    );
  }
}

VFDropdownBaseComponent.propTypes = {
  displayOptions: PropTypes.arrayOf(vFButtonSelectorOptionShape),
  updateValue: PropTypes.func,
  fieldTitle: PropTypes.string,
  placeholder: PropTypes.string,
  optionTemplate: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  inlineErrors: PropTypes.arrayOf(PropTypes.string),
  optional: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  touched: PropTypes.bool,
  clearTypeaheadListForFieldId: PropTypes.func,
  getDisplayOptionsOnMount: PropTypes.func,
  dispatchGetDisplayOptionsOnMount: PropTypes.func,
  getDisplayOptionsOnUpdate: PropTypes.func,
  dispatchGetDisplayOptionsOnUpdate: PropTypes.func,
  noClear: PropTypes.bool,
};




const mapStateToProps = (state, ownProps) => {

  const {
    optionTemplate,
    displayOptions,
    name, // refered to as fieldId outside of formik
  } = ownProps;


  const displayOptionsFromRedux = getDisplayOptionsFromRedux({
    state,
    optionTemplate,
    fieldId: name,
  });

  if (displayOptionsFromRedux.length > 0) {
    return ({
      displayOptions: [
        ...displayOptionsFromRedux,
        ...displayOptions,
      ],
    });
  }

  // dropdown base loads with it's ownProps
  return ({});


};

const mapDispatchToProps = (dispatch, ownProps) => ({
  clearTypeaheadListForFieldId: (fieldId) => {
    dispatch(clearTypeaheadListForFieldId(fieldId));
  },
  dispatchGetDisplayOptionsOnMount: () => {
    dispatch(ownProps.getDisplayOptionsOnMount());
  },
  dispatchGetDisplayOptionsOnUpdate: (value) => {
    dispatch(ownProps.getDisplayOptionsOnUpdate(value));
  },
});

// wrap VFDropdownComponent in 'withClickOutside' functionality
const VFDropdownBaseComponentWithClickOutside = withClickOutside(VFDropdownBaseComponent);


// Most of the time it's inefficent and uneeded to connect drop downs to redux. But when we do
// need them to be, we can just pass in withRedux and then this connects them :)
const VFDropdownBase = (props) => {
  if (props.withRedux) {

    const ComponentToRenderWithRedux = connect(
      mapStateToProps,
      mapDispatchToProps,
    )(VFDropdownBaseComponentWithClickOutside);
    // This is a curried component. So we need all props to bleed through. The layer above and below tracks what the props are
    /* eslint-disable-next-line react/jsx-props-no-spreading */
    return <ComponentToRenderWithRedux {...props} />;
  }

  // This is a curried component. So we need all props to bleed through. The layer above and below tracks what the props are
  /* eslint-disable-next-line react/jsx-props-no-spreading */
  return <VFDropdownBaseComponentWithClickOutside {...props} />;
};

VFDropdownBase.propTypes = {
  withRedux: PropTypes.bool,
};





export {
  VFDropdownBase,
};
